import i18n from '@/utils/i18n';
import { AddItemsCatalogBrowser } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/containers/add-items-catalog-browser';
import { ConvertToPriceQuoteModalContainer } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/containers/convert-to-price-quote-modal-container';
import { EditOrderGuideContextProvider } from '@/experiences/sales-portal/domain/order-guides/blocs/contexts/edit-order-guide-context';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { OrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { OrderGuideAutosaver } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/containers/order-guide-autosaver';
import { OrderGuideEditability } from '@/experiences/sales-portal/domain/order-guides/blocs/utils/editability-utils';
import { OrderGuideHeaderActions } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/containers/order-guide-header-actions';
import { OrderGuideItemsContent } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/containers/order-guide-items-content';
import { OrderGuideItemsSkeleton } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/components/order-guide-items-skeleton';
import { useOrderGuideItemsLoader } from '@/experiences/sales-portal/domain/order-guides/blocs/loaders/use-order-guide-items-loader';

interface Props {
  /** Account the price quote is created for; shown in the convert modal. */
  accountName: string;
  /** Chat the price quote is keyed by when converting. */
  chatUUID: string;
  collectionUUID: string;
  /** A just-created guide's empty contents; skips the items fetch when set. */
  createdOrderGuide?: OrderGuide;
  /** The selected guide's editability — seeds the edit-order-guide context. */
  editability: OrderGuideEditability;
  /** OG Partition to pre-select when the guide first loads (from navigation, not a url param)*/
  initialPartitionUUID?: string;
  isAddItemsOpen: boolean;
  isConvertToQuoteOpen: boolean;
  /** Distributor-locked guides collapse their items to the default group. */
  isDistributorLocked: boolean;
  onCloseAddItems: () => void;
  onCloseConvertToQuote: () => void;
  onConvertedToQuote: (priceQuoteId: string) => void;
  onOpenAddItems: () => void;
  onOpenConvertToQuote: () => void;
  onRefreshGuides: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

/**
 * Loads the selected order guide's items and renders them, keeping the items
 * area's loading and error states scoped here so the surrounding selectors stay
 * interactive while items resolve.
 */
export const OrderGuidesListContent: React.FC<Props> = ({
  accountName,
  chatUUID,
  collectionUUID,
  createdOrderGuide,
  editability,
  initialPartitionUUID,
  isAddItemsOpen,
  isConvertToQuoteOpen,
  isDistributorLocked,
  onCloseAddItems,
  onCloseConvertToQuote,
  onConvertedToQuote,
  onOpenAddItems,
  onOpenConvertToQuote,
  onRefreshGuides,
  restaurantUUID,
  supplierUUID,
}) => {
  const {
    error,
    loading,
    orderGuide: loadedOrderGuide,
    refetch,
  } = useOrderGuideItemsLoader({
    collectionUUID,
    isDistributorLocked,
    restaurantUUID,
    // A just-created guide is already known to be empty, so skip the fetch to improve UX.
    skip: !!createdOrderGuide,
    supplierUUID,
  });

  const orderGuide = loadedOrderGuide ?? createdOrderGuide;

  if (loading) {
    return <OrderGuideItemsSkeleton />;
  }

  if (error || !orderGuide) {
    return (
      <ErrorState
        description={i18n.t('sales_portal.error.description')}
        onPress={error ? () => void refetch() : onRefreshGuides}
        title={i18n.t('sales_portal.error.title')}
      />
    );
  }

  return (
    // The key guarantees that we remount if the collection changes
    <EditOrderGuideContextProvider
      editability={editability}
      initialSelectedPartitionUUID={initialPartitionUUID}
      key={collectionUUID}
      orderGuide={orderGuide}
    >
      {editability === 'editable' ? (
        <OrderGuideAutosaver
          lastSavedOrderGuide={orderGuide}
          restaurantUUID={restaurantUUID}
          supplierUUID={supplierUUID}
        />
      ) : null}
      <OrderGuideHeaderActions
        onOpenAddItems={onOpenAddItems}
        onOpenConvertToQuote={onOpenConvertToQuote}
      />
      <OrderGuideItemsContent onAddItems={onOpenAddItems} />
      <AddItemsCatalogBrowser
        isOpen={isAddItemsOpen}
        onClose={onCloseAddItems}
        restaurantUUID={restaurantUUID}
        supplierUUID={supplierUUID}
      />
      {isConvertToQuoteOpen ? (
        <ConvertToPriceQuoteModalContainer
          accountName={accountName}
          chatUUID={chatUUID}
          isOpen
          onClose={onCloseConvertToQuote}
          onConverted={onConvertedToQuote}
        />
      ) : null}
    </EditOrderGuideContextProvider>
  );
};
